import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { groupByDelivItem } from '../constants'

// One row per payload entry, but the checkbox sits on the line number and spans its
// lots: removing a line removes every lot reported against it, which is what the
// platform does and what the operator has to be able to see before confirming.
export default function PayloadTable({ entries, excluded, onToggle, onCorrect, disabled }) {
  const { t } = useTranslation()
  const key = suffix => `natura_operations.picking.${suffix}`
  const groups = groupByDelivItem(entries)

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm border border-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{t(key('column_remove'))}</th>
            <th className="px-3 py-2 text-left font-semibold">{t(key('column_deliv_item'))}</th>
            <th className="px-3 py-2 text-left font-semibold">{t(key('column_material'))}</th>
            <th className="px-3 py-2 text-left font-semibold">{t(key('column_batch'))}</th>
            <th className="px-3 py-2 text-right font-semibold">{t(key('column_quantity'))}</th>
            <th className="px-3 py-2 text-left font-semibold">{t(key('column_flags'))}</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const isExcluded = excluded.includes(group.delivItem)

            return group.entries.map((entry, index) => (
              <tr
                key={`${group.delivItem}-${entry.batch}-${index}`}
                className={`border-t border-gray-200 ${isExcluded ? 'bg-red-50 text-gray-400 line-through' : ''}`}
              >
                {index === 0 && (
                  <td rowSpan={group.entries.length} className="px-3 py-2 align-middle no-underline">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isExcluded}
                        disabled={disabled}
                        onChange={() => onToggle(group.delivItem)}
                        aria-label={t(key('remove_line_aria'), { line: group.delivItem })}
                      />
                      {group.entries.length > 1 && (
                        <span className="text-xs text-gray-500 no-underline">{t(key('lot_count'), { lots: group.entries.length })}</span>
                      )}
                    </label>
                  </td>
                )}
                {index === 0 && (
                  <td rowSpan={group.entries.length} className="px-3 py-2 font-mono align-middle">
                    {group.delivItem}
                  </td>
                )}
                <td className="px-3 py-2 font-mono break-all" title={entry.material}>
                  {entry.material_display || '—'}
                </td>
                <td className="px-3 py-2 font-mono break-all">{entry.batch || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{entry.dlv_qty}</td>
                <td className="px-3 py-2 font-mono text-xs">{Object.keys(entry.flags || {}).join(' ') || '—'}</td>
                {index === 0 && (
                  <td rowSpan={group.entries.length} className="px-3 py-2 text-right align-middle no-underline">
                    <button
                      type="button"
                      onClick={() => onCorrect(group.delivItem)}
                      disabled={disabled}
                      className="px-2 py-1 text-sm rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 no-underline"
                    >
                      {t(key('correct_line'))}
                    </button>
                  </td>
                )}
              </tr>
            ))
          })}
        </tbody>
      </table>
    </div>
  )
}

PayloadTable.propTypes = {
  entries: PropTypes.array.isRequired,
  excluded: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onCorrect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

PayloadTable.defaultProps = {
  disabled: false,
}
